import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ProgressIndicator, ProgressStep, Button } from "@carbon/react";
import i18next from "../../i18n";
import { BIDDING_TYPES } from "../../utils";
import { Loader } from "../../components";
import PageHeader from "../../components/carbon/PageHeader/PageHeader";
import ConfirmDialog from "../../components/carbon/ConfirmDialog/ConfirmDialog";
import BiddingTypeSelector from "../Order/BiddingTypeSelector/BiddingTypeSelector";
import ServiceStepStatus from "../Order/PriceDecrementPayment/ServiceDetailCard/ServiceStepStatus";
import { firstServiceGap, hasService } from "../Order/serviceDetail";
import PriceDecrementPayment from "../Order/PriceDecrementPayment/PriceDecrementPayment";
import MultipleMaterialsBiddingForm from "../Order/MultipleMaterialsBiddingForm/MultipleMaterialsBiddingForm";
import MultipleBiddingSummary from "../Order/MultipleBiddingSummary/MultipleBiddingSummary";
import StepSuppliers from "../Order/steps/StepSuppliers";
import { useFileInputUploader } from "../../hooks";
import { useBiddingSuppliers } from "../../hooks/useBiddingSuppliers";
import { useNotification, useOrderInvitation, useUser } from "../../context";
import { useSites } from "../../hooks/useSites";
import InlineCreateSiteModal from "./InlineCreateSiteModal/InlineCreateSiteModal";
import { SiteResponse } from "../../types/site.type";
import {
  actions,
  types,
  WIZARD_STEPS,
  initialState as requestInitialState,
} from "./orderByRequestConstants";
import { useOrderBuilder } from "../Order/useOrderBuilder";
import { useOrderByRequestPrefill } from "./useOrderByRequestPrefill";
import { useRequestOrderSubmission } from "./useRequestOrderSubmission";
import { useRequestStepGating } from "./useRequestStepGating";
import "../Order/order.scss";

const OrderByRequest = () => {
  const { invites, cleanUserInvites, createOrderInvitation } =
    useOrderInvitation();
  const { openNotification, openError } = useNotification();
  const { user, companyList } = useUser();
  const showCompany = (companyList?.length ?? 0) > 1;

  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(1);
  const [open, setOpen] = useState(false);
  const [siteModalLineId, setSiteModalLineId] = useState<string | null>(null);

  const { data: sites } = useSites(user?.company?.id ?? "");
  const { handleSubmit, reset } = useForm();

  const builder = useOrderBuilder({
    initialState: requestInitialState,
    resetAllSelectedOnInvite: true,
  });
  const {
    ordersToSubmit,
    setOrdersToSubmit,
    generalBidData,
    setGeneralBidData,
    selectedType,
    setSelectedType,
    selectedComplexType,
    setSelectedComplexType,
    isAllSelected,
    setIsAllSelected,
    handleSupplierSelection,
    handleSelectAll,
    handleSelectJointAll,
    handleOrdersToSubmit,
    handlePriceDecrementChanges,
    assignSiteToLine,
    handleAddressChange,
    applyUseOnceAddress,
    handleAddLineFile,
    handleRemoveLineFile,
    handleServiceDetailChange,
    handleApplyDatesToAll,
    handleSupplyPeriod,
  } = builder;

  const { isLoadingItems, isLoadingPurchaseRequests } =
    useOrderByRequestPrefill({
      user,
      ordersToSubmit,
      setOrdersToSubmit,
      setGeneralBidData,
      setSelectedType,
      setSelectedComplexType,
      setIsAllSelected,
      setActiveStep,
    });

  const { disabled, setDisabled, isServiceJoint, consultationViolation } =
    useRequestStepGating({
      activeStep,
      ordersToSubmit,
      selectedType,
      selectedComplexType,
      generalBidData,
    });

  const {
    isSubmitting,
    isCreatingOrder,
    isCreatingMultipleOrder,
    isUpdatingOrder,
    creatingIndividualOrders,
    onSubmit,
    onError,
  } = useRequestOrderSubmission({
    ordersToSubmit,
    generalBidData,
    selectedType,
    selectedComplexType,
    user,
    invites,
    cleanUserInvites,
    createOrderInvitation,
    openNotification,
    openError,
    navigate,
    closeDialog: () => setOpen(false),
    reset,
  });

  const {
    data: uploadedSpecificationFileUrl,
    mutate: uploadFile,
    isPending: isUploading,
    isSuccess: isUploadSuccess,
  } = useFileInputUploader();

  const { data: suppliers, mutate: getBiddingSuppliers } =
    useBiddingSuppliers();

  const resolvedBiddingType =
    selectedComplexType?.value === BIDDING_TYPES.JOINT
      ? selectedType?.value === BIDDING_TYPES.INVERSE
        ? BIDDING_TYPES.INVERSE_JOINT
        : BIDDING_TYPES.JOINT
      : selectedType?.value;

  const handleNext = (step: number) => {
    if (step === 1) {
      setActiveStep((prevState) => prevState + 1);
      const mat = ordersToSubmit?.map((m: any) => m.id);
      getBiddingSuppliers(mat as any);
      setDisabled(true);
    }
    if (activeStep === 2) setActiveStep((prevState) => prevState + 1);
    if (activeStep === 3) setActiveStep((prevState) => prevState + 1);
    if (activeStep === 4) setActiveStep((prevState) => prevState + 1);
    if (activeStep === 5) setOpen(true);
  };

  const handleSiteChange = (lineId: string, siteId: string) => {
    const site =
      (sites as SiteResponse[] | undefined)?.find((s) => s.id === siteId) ??
      null;
    assignSiteToLine(lineId, site);
  };

  const handleSiteCreated = (site: SiteResponse) => {
    if (siteModalLineId) assignSiteToLine(siteModalLineId, site);
    setSiteModalLineId(null);
  };

  const handleUseOnceAddress = ({ address }: { address: string }) => {
    if (!siteModalLineId) return;
    applyUseOnceAddress(siteModalLineId, address);
    setSiteModalLineId(null);
  };

  const handleSpecification = (e: any) => {
    const file = e?.target?.files;
    if (file) uploadFile(file[0]);
  };

  const isBusy =
    isLoadingPurchaseRequests ||
    isLoadingItems ||
    isCreatingOrder ||
    isCreatingMultipleOrder ||
    isUpdatingOrder ||
    creatingIndividualOrders;

  return (
    <div>
      <PageHeader title={i18next.t("navigation.newTender")} />
      {isBusy ? (
        <div className="order-loader">
          <Loader />
        </div>
      ) : (
        <div className="cds-form-surface">
          <ProgressIndicator
            currentIndex={activeStep - 1}
            spaceEqually
            className="order-builder__steps"
          >
            {WIZARD_STEPS.map(({ id, labelKey }) => (
              <ProgressStep key={id} label={i18next.t(labelKey)} />
            ))}
          </ProgressIndicator>

          {activeStep === 1 && (
            <BiddingTypeSelector
              types={types}
              onChangeType={(e) => setSelectedType(e)}
              typeValue={selectedType}
            />
          )}
          {activeStep === 2 && (
            <StepSuppliers
              suppliers={suppliers}
              selectedComplexType={selectedComplexType}
              isServiceJoint={isServiceJoint}
              isAllSelected={isAllSelected}
              onSelectAll={() => handleSelectAll(suppliers)}
              onSelectJointAll={() => handleSelectJointAll(suppliers)}
              ordersToSubmit={ordersToSubmit}
              invites={invites}
              handleSupplierSelection={handleSupplierSelection}
              handleOrdersToSubmit={handleOrdersToSubmit}
            />
          )}
          {activeStep === 3 && (
            <PriceDecrementPayment
              type={resolvedBiddingType}
              materialsToBid={ordersToSubmit}
              handlePriceDecrementChanges={handlePriceDecrementChanges}
              handleSupplyPeriod={handleSupplyPeriod}
              sites={sites}
              handleSiteChange={handleSiteChange}
              handleAddressChange={handleAddressChange}
              onCreateSite={(lineId: string) => setSiteModalLineId(lineId)}
              onApplyDatesToAll={handleApplyDatesToAll}
              onAddLineFile={handleAddLineFile}
              onRemoveLineFile={handleRemoveLineFile}
              onServiceDetailChange={handleServiceDetailChange}
              showCompany={showCompany}
            />
          )}
          {activeStep === 4 && (
            <MultipleMaterialsBiddingForm
              type={selectedComplexType.value}
              actions={actions}
              materialsToBid={ordersToSubmit}
              isLoadingUpload={isUploading}
              isUploadSuccess={isUploadSuccess}
              generalBidData={generalBidData}
              setGeneralBidData={setGeneralBidData}
              handleSpecification={handleSpecification}
              uploadedFileUrl={uploadedSpecificationFileUrl}
              handleSupplyPeriod={handleSupplyPeriod}
              onApplyDatesToAll={handleApplyDatesToAll}
              consultationViolation={consultationViolation}
            />
          )}
          {activeStep === 5 && (
            <MultipleBiddingSummary
              orderSummary={ordersToSubmit}
              generalBidData={generalBidData}
              type={selectedType.value}
              showCompany={showCompany}
            />
          )}

          <div className="order-actions">
            {activeStep === 3 && hasService(ordersToSubmit) && (
              <ServiceStepStatus
                gap={firstServiceGap(ordersToSubmit)}
                complete={!disabled}
              />
            )}
            <Button
              kind="secondary"
              onClick={() => setActiveStep((prevState) => prevState - 1)}
              disabled={activeStep === 1}
            >
              {i18next.t("cta.back")}
            </Button>
            <Button
              kind="primary"
              onClick={() => handleNext(activeStep)}
              disabled={disabled}
            >
              {activeStep === 5
                ? i18next.t("cta.launch")
                : i18next.t("cta.next")}
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={open}
        title={i18next.t("cta.launch")}
        body={i18next.t("order.question")}
        confirmLabel={i18next.t("cta.launch")}
        isPending={
          isCreatingOrder ||
          isCreatingMultipleOrder ||
          isUpdatingOrder ||
          isSubmitting
        }
        onConfirm={handleSubmit(onSubmit, onError)}
        onCancel={() => setOpen(false)}
      />
      <InlineCreateSiteModal
        open={siteModalLineId !== null}
        setOpen={(value: boolean) => !value && setSiteModalLineId(null)}
        onCreated={handleSiteCreated}
        onUseOnceAddress={handleUseOnceAddress}
      />
    </div>
  );
};

export default OrderByRequest;
